import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import { HlLobCode } from "../../../utils/constants";
import { useCallback, useEffect, useMemo, useReducer, useRef } from "react";
import {
  useLazyFetchDataServiceDetailsQuery,
  useLazyFetchHLFeatureEmailCountQuery,
  useLazyFetchHomeLoanIdentifierQuery,
  useLazyFetchLanDetailsQuery,
} from "../Homeloan_Api";
import {
  setHlCountDetails,
  setHomeLoanIdentifierData,
  setHomeLoanInfo,
  setSummaryData,
} from "../Homeloan_Slice";
import {
  LoanDetails,
  T_HlDataServiceApiRequest,
  T_HlFetchLanApiRequest,
  T_SummaryData,
} from "../Homeloan_Types";
import { isEmpty, uniq } from "lodash";
import { API_STATUS_CODE } from "../../../utils/ApiEndpoints";
import {
  HL_HandlingReducerAction,
  HL_ReduxInitialState,
} from "./HL_ReduxActionHandler";
import { T_LocalHLBaseData } from "./HL_BaseData_Types";
import { extractUniqueLoanAccountNumbers } from "../../../utils/helper";
import {
  filterDataByStatusAndDisbursement,
  getFunctionalityCodesForHL,
} from "../../../utils/conversionUtils";
import { setError } from "../../../redux-store/reducers/CsErrorSlice";

export const useHLBaseDataLoader = (
  lanNumber: string = "",
  isHookCalled: boolean = true,
  callOnlyOneLanApi: boolean = false,
): T_LocalHLBaseData => {
  const [localState, updateLocalState] = useReducer(
    HL_HandlingReducerAction,
    HL_ReduxInitialState,
  );
  const dispatch = useAppDispatch();
  const userDetails = useAppSelector((state) => state.CsAppSlice.userDetails);
  const allSummaryData: T_SummaryData[] = useAppSelector(
    (state) => state.HomeLoanSlice.summaryData,
  );
  const loanInfo = useAppSelector((state) => state.HomeLoanSlice.loanInfo);
  const customerAndLobDetails = userDetails?.customerAndLobDetails;
  const [fetchLanDetails, { error: isErrorInLanDetails }] =
    useLazyFetchLanDetailsQuery();
  const [fetchDataServiceDetails, { error: isErrorInDataServiceDetails }] =
    useLazyFetchDataServiceDetailsQuery();
  const [fetchHLFeatureEmailCountDetails] =
    useLazyFetchHLFeatureEmailCountQuery();
  const [fetchHomeLoanIdentifier, { error: isErrorInHomeLoanIdentifier }] =
    useLazyFetchHomeLoanIdentifierQuery();
  const quickActionMenuData = useAppSelector(
    (st) => st.GeneralAppElementsSlice.quickActionMenu,
  );
  const HLQuickActionMenuData = quickActionMenuData?.["04"];

  const entryCountDetails = useAppSelector(
    (state) => state.HomeLoanSlice.countDetails,
  );
  const homeLoanIdentifierData = useAppSelector(
    (state) => state.HomeLoanSlice.homeLoanIdentifierData,
  );
  const allLanNumberRef = useRef<string[]>([]);

  const allHomeLoanPans = useMemo(() => {
    return !isEmpty(lanNumber)
      ? [lanNumber] // Incase of lan received from props, use same
      : customerAndLobDetails
          ?.filter(
            (item) =>
              item.LOB === HlLobCode &&
              item.PRIMARY_SECONDARY_USER === "primary",
          )
          .filter((ln) => !isEmpty(ln?.PAN))
          .map((val) => val.PAN) || [];
  }, [lanNumber, customerAndLobDetails]);

  const callFetchCountDetailsApi = (accountNumberArray: string[]) => {
    if (!isEmpty(entryCountDetails)) {
      // Already have data, don't call API again
      return;
    }

    const functionalityCodeArray = getFunctionalityCodesForHL(
      HLQuickActionMenuData,
    ).filter((code): code is string => Boolean(code));

    fetchHLFeatureEmailCountDetails({
      loanAccountNumbers: accountNumberArray,
      functionalityCodes: functionalityCodeArray,
    }).then((res) => {
      if (
        res.error ||
        res.isError ||
        res.data?.code !== API_STATUS_CODE.SUCCESS
      ) {
        dispatch(
          setError({
            error: new Error(),
            message: "fetchCountDetails api failed",
          }),
        );
      }
      if (res.data?.code === API_STATUS_CODE.SUCCESS) {
        dispatch(setHlCountDetails(res.data?.payload));
      }
    });
  };

  const callFetchHomeLoanIdentifierApi = useCallback(async () => {
    if (
      !isEmpty(homeLoanIdentifierData) ||
      isEmpty(userDetails?.mobileNumber)
    ) {
      return;
    }

    await fetchHomeLoanIdentifier({
      MobileNumber: userDetails?.mobileNumber,
    }).then((res) => {
      if (
        res.error ||
        res.isError ||
        res.data?.code !== API_STATUS_CODE.SUCCESS
      ) {
        return;
      }

      const homeLoanIdentifierResData =
        res.data?.payload?.ResponseGetCustomerDetails?.DataArea?.CustomerDetails
          ?.Response || [];

      dispatch(setHomeLoanIdentifierData(homeLoanIdentifierResData));
    });
  }, [allSummaryData, homeLoanIdentifierData, userDetails?.mobileNumber]);

  const checkAndinitApiFlow = useCallback(() => {
    if (isEmpty(allHomeLoanPans)) {
      updateLocalState({
        type: "error",
        payload: Error("no pan number found for Home loan"),
      });
    } else if (
      (isEmpty(allSummaryData) || isEmpty(loanInfo.rows)) &&
      !localState.error
    ) {
      updateLocalState({ type: "fetching", payload: "" });
      checkAndInitDetails();
    } else {
      updateLocalState({ type: "success", payload: "" });
    }
  }, [allHomeLoanPans, allSummaryData, loanInfo, localState.error]);

  const api_fetchLanDetailsFromPan = async (panArray: string[]) => {
    let promises = uniq([...panArray]).map((item: string) => {
      const apiRequest: T_HlFetchLanApiRequest = {
        pan: item,
      };
      return fetchLanDetails(apiRequest, true);
    });

    return Promise.all(promises).then((results) => {
      return extractUniqueLoanAccountNumbers(results);
    });
  };

  useEffect(() => {
    if (!isEmpty(allLanNumberRef.current) && !isEmpty(HLQuickActionMenuData)) {
      callFetchCountDetailsApi(allLanNumberRef.current);
    }
  }, [allLanNumberRef.current, quickActionMenuData]);

  useEffect(() => {
    if (!isEmpty(allLanNumberRef.current)) {
      callFetchHomeLoanIdentifierApi();
    }
  }, [allLanNumberRef.current, userDetails?.mobileNumber]);

  const checkAndInitDetails = useCallback(async () => {
    let allLanNumbers = isEmpty(lanNumber)
      ? await api_fetchLanDetailsFromPan(allHomeLoanPans)
      : [lanNumber];
    if (allLanNumbers.length <= 0) {
      updateLocalState({ type: "error", payload: Error("Empty List") });
      return;
    }
    allLanNumberRef.current = allLanNumbers;
    if (callOnlyOneLanApi) {
      allLanNumbers = [allLanNumbers[0]];
    }
    let promises = [...allLanNumbers].map((item: string) => {
      const apiRequest: T_HlDataServiceApiRequest = {
        CCC: "",
        LAN: [
          {
            ID: item,
          },
        ],
      };
      return fetchDataServiceDetails(apiRequest, true);
    });
    let results = await Promise.all(promises);
    if (results.some((r) => r.data?.code !== API_STATUS_CODE.SUCCESS)) {
      updateLocalState({ type: "error", payload: Error("Api error") });
    } else {
      let transformedResult = results
        .flatMap((item: any) => item.data?.payload?.data?.Summary_Data || [])
        .filter((val: any) => !isEmpty(val));

      let finalTransformedResult =
        filterDataByStatusAndDisbursement(transformedResult);

      if (isEmpty(finalTransformedResult)) {
        updateLocalState({
          type: "error",
          payload: Error("Empty Summary Data"),
        });
      } else {
        dispatch(setSummaryData(finalTransformedResult));
        /** Process rows if there are transformed results */
        const rows =
          finalTransformedResult.length > 0
            ? finalTransformedResult
                .flatMap((row: T_SummaryData) => row.loan_info?.rows || [])
                .filter((val: LoanDetails | undefined) => !isEmpty(val))
            : [];
        if (isEmpty(rows)) {
          updateLocalState({
            type: "error",
            payload: Error("Empty List for Loan Info"),
          });
        } else {
          dispatch(
            setHomeLoanInfo({
              totalRows: `${rows.length}`,
              moreRows: "false",
              rows,
            }),
          );
        }
      }
    }
  }, [allHomeLoanPans, userDetails?.mobileNumber]);

  useEffect(() => {
    if (isHookCalled) {
      checkAndinitApiFlow();
    }
  }, [allHomeLoanPans, allSummaryData, loanInfo, isHookCalled]);

  useEffect(() => {
    if (
      isErrorInLanDetails ||
      isErrorInDataServiceDetails ||
      isErrorInHomeLoanIdentifier
    ) {
      updateLocalState({
        type: "error",
        payload: (isErrorInLanDetails ||
          isErrorInDataServiceDetails ||
          isErrorInHomeLoanIdentifier ||
          Error("api failed")) as Error,
      });
    }
  }, [
    isErrorInLanDetails,
    isErrorInDataServiceDetails,
    isErrorInHomeLoanIdentifier,
  ]);

  return localState;
};
